/**
 * InboxTestPage — test form for the in-app inbox channel (roadmap #76).
 * Sends store messages per account+user; the "Ver buzón" panel reads them
 * back through GET /v1/inbox/{userId} so the whole loop can be exercised
 * without leaving the page.
 */

import React, { useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  MarkEmailReadOutlined as ReadIcon,
} from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  listInbox,
  markInboxAllRead,
  sendInboxTestNotification,
  type InboxListResult,
} from "../services/testService";
import AccountAppPicker from "./sections/AccountAppPicker";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

interface InboxFormValues {
  accountId: string;
  appId: string;
  userIds: { value: string }[];
  title: string;
  text: string;
  link: string;
}

const emptyValues = (): InboxFormValues => ({
  accountId: "",
  appId: "",
  userIds: [{ value: "" }],
  title: "",
  text: "",
  link: "",
});

export const InboxTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const [submitting, setSubmitting] = useState(false);
  const [inboxView, setInboxView] = useState<InboxListResult | null>(null);

  const methods = useForm<InboxFormValues>({
    defaultValues: emptyValues(),
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "userIds",
  });

  const accountId = methods.watch("accountId");
  const firstUserId = methods.watch("userIds.0.value");

  const onSubmit = async (values: InboxFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const response = await sendInboxTestNotification({
        accountId: values.accountId,
        appId: values.appId,
        recipients: {
          type: "INDIVIDUAL",
          userIds: values.userIds.map((u) => u.value.trim()).filter(Boolean),
        },
        message: {
          title: values.title.trim() || undefined,
          content: values.text,
          link: values.link.trim() || undefined,
        },
        origin: "admin-test",
      });
      if (response.success !== false) {
        notify("resources.test.notifications.inboxSent", { type: "success" });
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.inboxSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.inboxSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadInbox = async (): Promise<void> => {
    if (!accountId || !firstUserId?.trim()) return;
    try {
      setInboxView(await listInbox(accountId, firstUserId.trim()));
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.inboxLoadFailed",
          translate,
        ),
        { type: "error" },
      );
    }
  };

  const markAll = async (): Promise<void> => {
    if (!accountId || !firstUserId?.trim()) return;
    await markInboxAllRead(accountId, firstUserId.trim());
    await loadInbox();
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.inbox.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.recipients", {
            _: "Recipients",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12}>
              {fields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: idx < fields.length - 1 ? 1.5 : 0,
                  }}
                >
                  <Controller
                    name={`userIds.${idx}.value`}
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.botChat.userIdRequired",
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        label={`${translate("resources.test.botChat.userId")} ${fields.length > 1 ? idx + 1 : ""}`.trim()}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ??
                          translate("resources.test.inbox.userIdHelp")
                        }
                      />
                    )}
                  />
                  {fields.length > 1 && (
                    <IconButton
                      aria-label={translate("ra.action.remove")}
                      onClick={() => remove(idx)}
                      sx={{ alignSelf: "center" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {idx === fields.length - 1 && fields.length < 20 && (
                    <IconButton
                      aria-label={translate("ra.action.add")}
                      onClick={() => append({ value: "" })}
                      sx={{ alignSelf: "center" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="title"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.teamChat.titleField")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="link"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.inbox.linkField")}
                    placeholder="/bookings/123"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="text"
                control={methods.control}
                rules={{
                  required: translate("resources.test.botChat.textRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    label={translate("resources.test.botChat.textField")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.inbox.viewSection", {
            _: "Inbox of the first user",
          })}
          last
        >
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!accountId || !firstUserId?.trim()}
              onClick={() => void loadInbox()}
            >
              {translate("resources.test.inbox.viewButton")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReadIcon sx={{ fontSize: 15 }} />}
              disabled={!inboxView || inboxView.unreadCount === 0}
              onClick={() => void markAll()}
            >
              {translate("resources.test.inbox.markAllRead")}
            </Button>
            {inboxView && (
              <Typography sx={{ fontSize: 13, alignSelf: "center", color: rg.text2 }}>
                {translate("resources.test.inbox.unread", {
                  smart_count: inboxView.unreadCount,
                  _: `${inboxView.unreadCount} unread / ${inboxView.recordsTotal} total`,
                })}
              </Typography>
            )}
          </Box>
          {inboxView?.data.map((m) => (
            <Box
              key={m.id}
              sx={{
                border: `1px solid ${rg.border}`,
                borderRadius: "10px",
                p: "10px 12px",
                mb: 1,
                opacity: m.isRead ? 0.6 : 1,
                backgroundColor: rg.bgElev,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: m.isRead ? 400 : 700 }}>
                {m.title || "—"}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: rg.text2 }}>
                {m.body}
              </Typography>
              <Typography sx={{ fontSize: 11, color: rg.text3, fontFamily: rg.fontMono }}>
                {new Date(m.createdAt).toLocaleString()} · {m.isRead ? "read" : "unread"}
                {m.link ? ` · ${m.link}` : ""}
              </Typography>
            </Box>
          ))}
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default InboxTestPage;
